import type { PlannedRoutine, PlannedSession } from "@akina/domain";
import { create } from "zustand";
import { useTherapyPlanManagementViewStore } from "./useTherapyPlanManagementViewStore";
import type { SessionEditWarnings, TherapyPlanManagementState, TherapyPlanManagementData } from "../therapyPlanManagementTypes";

const DEFAULT_LATERALITY = "both" as const;

export const MIN_ROUTINES_WARNING = 3;
export const MAX_ROUTINES_WARNING = 6;
export const MAX_ALLOWED_ROUTINES = 9;

export type Blocker = {
  state: "blocked" | "unblocked" | "proceeding";
  reset?: () => void;
  proceed?: () => void;
};

class TherapyPlanManagementEditor {
  constructor(private readonly data: TherapyPlanManagementData) {}

  public updateSession(session: PlannedSession) {
    const newData = this.clone();
    newData.sessions = newData.sessions.map(s => (s.key === session.key ? session : s));
    return newData;
  }

  public addRoutine(sessionKey: string, routine: PlannedRoutine) {
    const session = this.data.sessions.find(s => s.key === sessionKey);
    if (!session) return this.data;

    const newData = this.clone();
    const targetSession = newData.sessions.find(s => s.key === sessionKey);
    if (!targetSession) return this.data;

    const newRoutinePlanKeySequence = [...targetSession.routinePlanKeySequence, routine.key];
    const newRoutinePlans = [...targetSession.routinePlans, routine].toSorted(
      (a, b) => newRoutinePlanKeySequence.indexOf(a.key) - newRoutinePlanKeySequence.indexOf(b.key),
    );

    targetSession.routinePlans = newRoutinePlans;
    targetSession.routinePlanKeySequence = newRoutinePlanKeySequence;
    return newData;
  }

  public updateRoutine(sessionKey: string, routine: PlannedRoutine, oldKey: string) {
    const session = this.data.sessions.find(s => s.key === sessionKey);
    if (!session) return this.data;

    const newData = this.clone();
    const targetSession = newData.sessions.find(s => s.key === sessionKey);
    if (!targetSession) return this.data;

    const newRoutinePlanKeySequence = targetSession.routinePlanKeySequence.map(key =>
      key === oldKey ? routine.key : key,
    );
    const newRoutinePlans = targetSession.routinePlans
      .map(r => (r.key === oldKey ? routine : r))
      .toSorted((a, b) => newRoutinePlanKeySequence.indexOf(a.key) - newRoutinePlanKeySequence.indexOf(b.key));

    targetSession.routinePlans = newRoutinePlans;
    targetSession.routinePlanKeySequence = newRoutinePlanKeySequence;
    return newData;
  }

  private clone() {
    return structuredClone(this.data);
  }
}

export const useTPMEditorStore = create<TherapyPlanManagementState>((set, get) => ({
  isEditing: false,
  isSaving: false,
  sessionKey: undefined,
  editWarning: undefined,
  modalOpen: null,
  discardChangesParams: { onConfirm: undefined },
  confirmPhaseChangeParams: { targetPhaseKey: undefined, phaseNumber: undefined, patientName: undefined },
  addRoutineParams: { selectedRoutines: [] },
  editRoutineParams: undefined,

  actions: {
    cleanup: () =>
      set({
        isEditing: false,
        isSaving: false,
        sessionKey: undefined,
        editWarning: undefined,
        modalOpen: null,
        editRoutineParams: undefined,
        addRoutineParams: { selectedRoutines: [] },
      }),

    setIsEditing: isEditing => set({ isEditing }),
    setSessionKey: sessionKey => set({ sessionKey }),

    setModalOpen: type => set({ modalOpen: type }),
    setDiscardChangesParams: params => set({ discardChangesParams: params }),
    setConfirmPhaseChangeParams: params => set({ confirmPhaseChangeParams: params }),
    setEditRoutineParams: params => set({ editRoutineParams: params }),
    setAddRoutineParams: params => {
      const current = get().addRoutineParams;
      set({ addRoutineParams: { ...current, ...params } });
    },
    closeModal: () =>
      set({
        modalOpen: null,
        discardChangesParams: { onConfirm: undefined },
        confirmPhaseChangeParams: { targetPhaseKey: undefined, phaseNumber: undefined, patientName: undefined },
        addRoutineParams: { selectedRoutines: [] },
        editRoutineParams: undefined,
      }),

    startUpdate: (session, phaseKey) => {
      const { initialData } = useTherapyPlanManagementViewStore.getState();
      if (!initialData) return;
      useTherapyPlanManagementViewStore.getState().actions.setData(structuredClone(initialData));
      useTherapyPlanManagementViewStore.getState().actions.setPhaseKey(phaseKey);
      set({ isEditing: true, sessionKey: session.key });
      get().actions.updateEditWarnings(session);
    },

    cancelUpdate: () => {
      const { initialData } = useTherapyPlanManagementViewStore.getState();
      if (!initialData) return;
      useTherapyPlanManagementViewStore.getState().actions.setData(structuredClone(initialData));
      set({ isEditing: false, sessionKey: undefined, editWarning: undefined });
    },

    saveUpdate: async (onSuccess) => {
      const { strategy, data } = useTherapyPlanManagementViewStore.getState();
      const { sessionKey } = get();
      if (!strategy || !data || !sessionKey) return;

      const session = data.sessions.find(s => s.key === sessionKey);
      if (!session) return;

      const sanitizedSession = {
        ...session,
        routinePlans: session.routinePlans.map(routine => ({
          ...routine,
          laterality: routine.hasLaterality ? (routine.laterality ?? DEFAULT_LATERALITY) : DEFAULT_LATERALITY,
        })),
      };

      set({ isSaving: true });
      try {
        await strategy.saveSession(sanitizedSession);
        useTherapyPlanManagementViewStore.setState({ initialData: structuredClone(data) });
        set({ isEditing: false, sessionKey: undefined, editWarning: undefined });
        onSuccess?.();
      } catch (error) {
        set({ isEditing: true });
        throw error;
      } finally {
        set({ isSaving: false });
      }
    },

    updatePlannedSession: session => {
      if (!session) return;
      const { data } = useTherapyPlanManagementViewStore.getState();
      if (!data) return;

      const updated = new TherapyPlanManagementEditor(data).updateSession(session);
      useTherapyPlanManagementViewStore.getState().actions.setData(updated);
      get().actions.updateEditWarnings(session);
    },

    updatePlannedRoutine: (routine, oldKey) => {
      if (!routine) return;
      const { data } = useTherapyPlanManagementViewStore.getState();
      const { sessionKey } = get();
      if (!data || !sessionKey) return;

      const updated = new TherapyPlanManagementEditor(data).updateRoutine(sessionKey, routine, oldKey);
      useTherapyPlanManagementViewStore.getState().actions.setData(updated);
      const updatedSession = updated.sessions.find(s => s.key === sessionKey);
      get().actions.updateEditWarnings(updatedSession ?? null);
    },

    addPlannedRoutine: routine => {
      const { data } = useTherapyPlanManagementViewStore.getState();
      const { sessionKey } = get();
      if (!data || !sessionKey) return;

      const updated = new TherapyPlanManagementEditor(data).addRoutine(sessionKey, routine);
      useTherapyPlanManagementViewStore.getState().actions.setData(updated);
      const updatedSession = updated.sessions.find(s => s.key === sessionKey);
      get().actions.updateEditWarnings(updatedSession ?? null);
    },

    updateEditWarnings: session => {
      const count = session?.routinePlans?.length ?? 0;
      let warning: SessionEditWarnings | undefined;
      if (count < MIN_ROUTINES_WARNING) warning = "sessionTooShort";
      else if (count > MAX_ROUTINES_WARNING) warning = "sessionTooLong";
      set({ editWarning: warning });
    },
  },
}));
